import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormControl, Skeleton } from '@mui/material';
import {
  ITBox,
  QboAlert,
  QboButton,
  QboCard,
  QboCheckbox,
  QboFormGroup,
  QboTypography,
} from '@ui/Components';

function TextWithLabel({ title, description, color }) {
  return (
    <div style={{ display: 'flex', marginTop: 7 }}>
      <QboTypography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</QboTypography>
      <QboTypography wrap sx={{ fontSize: 13, color: { color } }}>
        &nbsp;{description}
      </QboTypography>
    </div>
  );
}

TextWithLabel.propTypes = {
  title: PropTypes.string,
  color: PropTypes.string,
  description: PropTypes.string,
};

TextWithLabel.defaultProps = {
  title: '',
  color: '',
  description: '',
};

export default function TransferSubresellerFormConfirmSectionPage({
  confirmingTheAboveInfoAttributes,
  formData,
  onSubmit,
  onBack,
  resetForm,
}) {
  const { t } = useTranslation();

  const transferTypeTitles = {
    1: 'Transfer sub-reseller between distributor',
    2: 'Transfer direct reseller to distributor as sub-reseller',
    3: 'Transfer sub-reseller from distributor to make direct-reseller',
  };

  const { transferAggregatePartnerState } = useSelector(
    (state) => state.transferAggregatePartnerState
  );

  // Submit is considered successful if the ones that succeed validation has been submitted
  const submitSuccess = useMemo(
    () =>
      (transferAggregatePartnerState.results?.some((result) => result.submitSuccess) ?? false) &&
      (transferAggregatePartnerState.results?.every(
        (result) => !result.validateResult.validationSuccess || result.submitSuccess
      ) ??
        false),
    [transferAggregatePartnerState.results]
  );

  const submitErrors = useMemo(
    () =>
      transferAggregatePartnerState.results
        ?.filter((result) => !!result.submitError)
        .map(
          (result) => (result.region ? `${result.region.toUpperCase()}: ` : '') + result.submitError
        ),
    [transferAggregatePartnerState.results]
  );

  useEffect(() => {
    if (submitSuccess) {
      onBack();
      resetForm();
    }
  }, [submitSuccess]);

  const allowSubmit = useMemo(
    () =>
      transferAggregatePartnerState.results?.length &&
      transferAggregatePartnerState.results.some(
        (result) => result.validateResult?.validationSuccess
      ),
    [transferAggregatePartnerState.results]
  );

  const partialSubmit = useMemo(
    () =>
      transferAggregatePartnerState.results?.length &&
      transferAggregatePartnerState.results.some(
        (result) => result.validateResult?.validationSuccess
      ) &&
      transferAggregatePartnerState.results.some(
        (result) => !result.validateResult?.validationSuccess
      ),
    [transferAggregatePartnerState.results]
  );

  const skeletons = useMemo(
    () => (
      <QboCard className="TransferSubresellerPage__information-wrapper" isBordered>
        <ITBox className="TransferSubresellerPage__information-box" direction="column">
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="70%" />
          <hr />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="70%" />
        </ITBox>
      </QboCard>
    ),
    []
  );

  return (
    <ITBox className="TransferSubscriptionFormPage__wrapper" direction="column">
      {submitSuccess && (
        <QboAlert type="success" style={{ fontSize: '1.15em' }}>
          Successfully submitted
        </QboAlert>
      )}
      {transferAggregatePartnerState?.errorMessage && (
        <QboAlert type="error" style={{ fontSize: '1.15em' }}>
          {Array.isArray(transferAggregatePartnerState.errorMessage)
            ? transferAggregatePartnerState.errorMessage.map((e, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <p key={i} style={{ color: 'currentColor' }}>
                  {e}
                </p>
              ))
            : transferAggregatePartnerState.errorMessage}
        </QboAlert>
      )}
      {!!submitErrors?.length && (
        <QboAlert type="error" style={{ fontSize: '1.15em' }}>
          {submitErrors.map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={i} style={{ color: 'currentColor' }}>
              {e}
            </p>
          ))}
          <p style={{ color: 'currentColor' }}>
            {t('transfer_partner_page.modal.form.submit_failed')}
          </p>
        </QboAlert>
      )}

      {partialSubmit && (
        <QboAlert type="warning" style={{ fontSize: '1.15em' }}>
          {t('transfer_partner_page.modal.form.partial_submit')}
        </QboAlert>
      )}

      {!!transferAggregatePartnerState.inProgress && skeletons}

      {!!transferAggregatePartnerState.isAggregate && (
        <QboAlert
          variant="outlined"
          type="info"
          style={{
            border: '1px solid #ebebeb',
            background: '#f3f3f3',
            fontSize: '10px !important',
            marginTop: '20px',
          }}>
          <QboTypography
            noWrap={false}
            style={{ fontSize: '13px', color: '#767676', whiteSpace: 'pre-line' }}>
            {t('transfer_partner_page.modal.form.is_aggregate')}
          </QboTypography>
        </QboAlert>
      )}

      {transferAggregatePartnerState.results?.length &&
        transferAggregatePartnerState.results.map((result) => (
          <QboCard
            className="TransferSubresellerPage__information-wrapper"
            titleCard={`${t('transfer_partner_page.modal.data_list.req_info_title_text')} ${
              result.region ? `(${result.region.toUpperCase()})` : ''
            }`}
            isBordered>
            <ITBox className="TransferSubresellerPage__information-box" direction="column">
              <TextWithLabel
                color={result.validateResult?.validationSuccess ? '#4F8A10' : '#D63301'}
                title={t('transfer_partner_page.modal.data_list.validation_success')}
                description={result.validateResult?.validationSuccess ? 'true' : 'false'}
              />
              {result.validateResult?.errorDescription && (
                <TextWithLabel
                  color="#D63301"
                  title={t('transfer_partner_page.modal.data_list.error_description')}
                  description={result.validateResult?.errorDescription}
                />
              )}
              {result.errorMessage && (
                <TextWithLabel
                  color="#D63301"
                  title={t('transfer_partner_page.modal.data_list.error_description')}
                  description={result.errorMessage}
                />
              )}
              <hr />
              <TextWithLabel
                title={t('transfer_partner_page.modal.data_list.transfer_type')}
                description={
                  transferTypeTitles[
                    result.validateResult?.transferType ?? result.parameters?.transferType ?? ''
                  ]
                }
              />
              {!!result.parameters?.sourceDistributorID && (
                <TextWithLabel
                  title={t('transfer_partner_page.modal.data_list.source_distributor')}
                  description={`${result.validateResult?.sourceDistributorName ?? ''} (${
                    result.validateResult?.sourceDistributorID ??
                    result.parameters?.sourceDistributorID ??
                    ''
                  })`}
                />
              )}
              <TextWithLabel
                title={t('transfer_partner_page.modal.data_list.reseller_to_transder')}
                description={`${result.validateResult?.resellerToTransferName ?? ''} (${
                  result.validateResult?.resellerIDToBeTransferred ??
                  result.parameters?.resellerIDToBeTransferred ??
                  ''
                })`}
              />
              {!!result.parameters?.targetDistributorID && (
                <TextWithLabel
                  title={t('transfer_partner_page.modal.data_list.target_distributor')}
                  description={`${result.validateResult?.targetDistributorName ?? ''} (${
                    result.validateResult?.targetDistributorID ??
                    result.parameters?.targetDistributorID ??
                    ''
                  })`}
                />
              )}
              <hr />
              <TextWithLabel
                title={t('transfer_partner_page.modal.data_list.description')}
                description={result.validateResult?.description || result.parameters?.description}
              />
              <TextWithLabel
                title={t('transfer_partner_page.modal.data_list.support_ticket')}
                description={
                  result.validateResult?.['support-ticket'] || result.parameters?.['support-ticket']
                }
              />
              <TextWithLabel
                title={t('transfer_partner_page.modal.data_list.escalation_ticket')}
                description={
                  result.validateResult?.['escalation-ticket'] ||
                  result.validateResult?.['escalation-ticket']
                }
              />
            </ITBox>
          </QboCard>
        ))}

      <QboFormGroup>
        <FormControl className="FormControl__default-wrapper FormControl__full-width-wrapper">
          <QboCheckbox
            sx={{ fontSize: '12px !important' }}
            className="QboCheckbox__wrapper_text"
            label={t('transfer_partner_page.modal.form.confirming_the_above_information')}
            checked={confirmingTheAboveInfoAttributes.value}
            onChange={(e) => {
              confirmingTheAboveInfoAttributes.handleChange(e.target.checked);
            }}
          />
          {confirmingTheAboveInfoAttributes.errorMessage && (
            <span style={{ height: '15px !important' }} className="QboTextField__warning-text">
              {/* <WarningAmberIcon className="QboTextField__warning-icon" /> */}
              {confirmingTheAboveInfoAttributes.errorMessage}
            </span>
          )}
        </FormControl>
      </QboFormGroup>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <QboButton
          disabled={transferAggregatePartnerState.inProgress}
          sx={{ width: '100px !important' }}
          variant="outlined"
          color="secondary"
          onClick={() => onBack()}>
          {t('transfer_subreseller_page.view_data.back_button_text')}
        </QboButton>
        <QboButton
          disabled={
            !allowSubmit ||
            transferAggregatePartnerState.inProgress ||
            transferAggregatePartnerState.isSubmitting ||
            !confirmingTheAboveInfoAttributes.value
          }
          sx={{ width: '150px !important', justifyContent: 'end' }}
          variant="contained"
          onClick={onSubmit}>
          {t('transfer_subreseller_page.view_data.transfer_button_text')}
        </QboButton>
      </div>
    </ITBox>
  );
}

TransferSubresellerFormConfirmSectionPage.propTypes = {
  confirmingTheAboveInfoAttributes: PropTypes.object,
  onSubmit: PropTypes.func,
  onBack: PropTypes.func,
  resetForm: PropTypes.func,
  formData: PropTypes.object,
};

TransferSubresellerFormConfirmSectionPage.defaultProps = {
  confirmingTheAboveInfoAttributes: {},
  formData: {},
  onSubmit: () => {},
  onBack: () => {},
  resetForm: () => {},
};
